// @oasis-permission-inherits: /projects (projects:feed-view, projects:view)
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  Copy,
  ExternalLink,
  GitCommit,
  Languages,
  Loader2,
  MessageSquare,
  RefreshCw,
  User,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { copyToClipboard } from "~/lib/clipboard";
import { useAuthStore } from "~/modules/auth/auth-store";
import {
  chunkProjectChatTranslationItems,
  getProjectChatTranslateButtonLabel,
  type ProjectChatTranslationItem,
} from "~/modules/projects/lib/project-chat-inspection";
import { formatTimestamp, parseDateTime } from "~/modules/projects/lib/project-detail-format";
import {
  translateProjectChatTextMutation,
  type ProjectChatTranslatedItem,
} from "~/modules/projects/mutations";
import {
  getProjectChatFeedQuery,
  PROJECT_CHAT_FEED_MESSAGE_LIMIT,
  PROJECT_CHAT_FEED_PAGE_SIZE,
} from "~/modules/projects/queries";
import type { ProjectChatFeedItem } from "~/modules/projects/schemas";

export const Route = createFileRoute("/_dash/projects/chats")({
  component: RouteComponent,
});

type ProjectChatCachedTranslation = ProjectChatTranslatedItem & {
  sourceText: string;
};
type ProjectChatTranslationMap = Record<string, ProjectChatCachedTranslation>;
type ProjectChatPreviewRole = keyof ProjectChatFeedItem["previews"];

function collectProjectChatFeedTranslationItems(
  threadSnapshots: ProjectChatFeedItem[]
): ProjectChatTranslationItem[] {
  return threadSnapshots.flatMap((threadSnapshot) =>
    (["user", "assistant"] as const).flatMap((role) => {
      const text = threadSnapshot.previews[role].trim();
      return text ? [{ id: getThreadPreviewTranslationId(threadSnapshot, role), text }] : [];
    })
  );
}

function getThreadPreviewTranslationId(
  threadSnapshot: ProjectChatFeedItem,
  role: ProjectChatPreviewRole
) {
  return `thread:${threadSnapshot.thread.threadId}:${role}:preview`;
}

function RouteComponent() {
  const [user] = useAuthStore((s) => [s.user]);
  const canOpenProjects = user?.permissions.includes("projects:view") ?? false;
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = getProjectChatFeedQuery.useInfiniteQuery({
    params: {
      limit: PROJECT_CHAT_FEED_PAGE_SIZE,
      messageLimit: PROJECT_CHAT_FEED_MESSAGE_LIMIT,
    },
  });
  const threadSnapshots = useMemo(
    () => data?.pages.flatMap((page) => page.threads.page).filter((item) => item.isTerminal) ?? [],
    [data?.pages]
  );
  const [translationsById, setTranslationsById] = useState<ProjectChatTranslationMap>({});
  const [showTranslations, setShowTranslations] = useState(true);
  const [translationRetryGeneration, setTranslationRetryGeneration] = useState(0);
  const [translationSettlementGeneration, setTranslationSettlementGeneration] = useState(0);
  const inFlightTranslations = useRef(new Set<string>());
  const failedTranslationGeneration = useRef(new Map<string, string>());
  const activeTranslationTexts = useRef(new Map<string, string>());
  const isMounted = useRef(false);
  const translationItems = useMemo(
    () => collectProjectChatFeedTranslationItems(threadSnapshots),
    [threadSnapshots]
  );
  const translationItemsKey = useMemo(() => JSON.stringify(translationItems), [translationItems]);
  const translationGeneration = `${translationRetryGeneration}:${translationItemsKey}`;
  const currentTranslationGeneration = useRef(translationGeneration);
  const hasTranslations = Object.keys(translationsById).length > 0;
  const { mutateAsync: translateChat, isPending: isTranslating } =
    translateProjectChatTextMutation.useMutation();
  const translateButtonLabel = getProjectChatTranslateButtonLabel({
    isTranslating,
    showTranslations,
    hasTranslations,
  });

  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    currentTranslationGeneration.current = translationGeneration;
    activeTranslationTexts.current = new Map(translationItems.map((item) => [item.id, item.text]));
  }, [translationGeneration, translationItems]);

  useEffect(() => {
    const activeTranslationItemsById = new Map(translationItems.map((item) => [item.id, item]));

    setTranslationsById((currentTranslations) => {
      const nextTranslations = Object.fromEntries(
        Object.entries(currentTranslations).filter(([id, translation]) => {
          return activeTranslationItemsById.get(id)?.text === translation.sourceText;
        })
      ) as ProjectChatTranslationMap;

      return Object.keys(nextTranslations).length === Object.keys(currentTranslations).length
        ? currentTranslations
        : nextTranslations;
    });
  }, [translationItems]);

  useEffect(() => {
    const untranslatedItems = translationItems.filter((item) => {
      const requestKey = JSON.stringify([item.id, item.text]);
      if (
        translationsById[item.id]?.sourceText === item.text ||
        inFlightTranslations.current.has(requestKey) ||
        failedTranslationGeneration.current.get(requestKey) === translationGeneration
      ) {
        return false;
      }
      inFlightTranslations.current.add(requestKey);
      return true;
    });
    if (untranslatedItems.length === 0) {
      return;
    }

    const chunks = chunkProjectChatTranslationItems(untranslatedItems);
    void Promise.allSettled(chunks.map((items) => translateChat({ params: { items } }))).then(
      (results) => {
        const translatedEntries: [string, ProjectChatCachedTranslation][] = [];
        let firstError: unknown;
        const failedItems: ProjectChatTranslationItem[] = [];
        results.forEach((result, index) => {
          const items = chunks[index];
          items.forEach((item) => {
            inFlightTranslations.current.delete(JSON.stringify([item.id, item.text]));
          });
          if (result.status === "rejected") {
            firstError ??= result.reason;
            failedItems.push(...items);
            items.forEach((item) => {
              failedTranslationGeneration.current.set(
                JSON.stringify([item.id, item.text]),
                translationGeneration
              );
            });
            return;
          }

          const responseById = new Map(result.value.items.map((item) => [item.id, item]));
          items.forEach((item) => {
            const translation = responseById.get(item.id);
            if (translation) {
              translatedEntries.push([item.id, { ...translation, sourceText: item.text }]);
            } else {
              failedItems.push(item);
              failedTranslationGeneration.current.set(
                JSON.stringify([item.id, item.text]),
                translationGeneration
              );
            }
          });
        });

        if (!isMounted.current) {
          return;
        }
        if (
          currentTranslationGeneration.current !== translationGeneration &&
          failedItems.some((item) => activeTranslationTexts.current.get(item.id) === item.text)
        ) {
          setTranslationSettlementGeneration((generation) => generation + 1);
        }
        if (translatedEntries.length > 0) {
          setTranslationsById((currentTranslations) => {
            const currentEntries = translatedEntries.filter(
              ([id, translation]) =>
                activeTranslationTexts.current.get(id) === translation.sourceText
            );
            return currentEntries.length > 0
              ? { ...currentTranslations, ...Object.fromEntries(currentEntries) }
              : currentTranslations;
          });
        }
        if (
          firstError &&
          currentTranslationGeneration.current === translationGeneration &&
          failedItems.some((item) => activeTranslationTexts.current.get(item.id) === item.text)
        ) {
          toast.error(
            firstError instanceof Error ? firstError.message : "Failed to translate chat"
          );
        }
      }
    );
  }, [
    translationItems,
    translationGeneration,
    translationSettlementGeneration,
    translationsById,
    translateChat,
  ]);

  const handleTranslateClick = async () => {
    if (showTranslations && hasTranslations) {
      setShowTranslations(false);
      return;
    }

    const untranslatedItems = translationItems.filter(
      (item) => translationsById[item.id]?.sourceText !== item.text
    );

    if (untranslatedItems.length === 0 && hasTranslations) {
      setShowTranslations(true);
      return;
    }

    if (untranslatedItems.length === 0) {
      toast.info("No chat text to translate");
      return;
    }

    try {
      const translatedItems = (
        await Promise.all(
          chunkProjectChatTranslationItems(untranslatedItems).map((items) =>
            translateChat({ params: { items } })
          )
        )
      ).flatMap((response) => response.items);
      const translatedCount = translatedItems.filter((item) => item.translated).length;
      const untranslatedItemsById = new Map(untranslatedItems.map((item) => [item.id, item]));

      setTranslationsById(
        (currentTranslations) =>
          ({
            ...currentTranslations,
            ...Object.fromEntries(
              translatedItems.flatMap((item) => {
                const sourceText = untranslatedItemsById.get(item.id)?.text;
                return sourceText ? [[item.id, { ...item, sourceText }]] : [];
              })
            ),
          }) as ProjectChatTranslationMap
      );
      setShowTranslations(true);
      toast.success(
        translatedCount > 0
          ? `Translated ${translatedCount} chat item${translatedCount === 1 ? "" : "s"}`
          : "No non-English chat text found"
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to translate chat");
    }
  };

  return (
    <div className="min-w-0 space-y-5 pb-12">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold">Project Chats</h1>
          <p className="text-sm text-muted-foreground">Chronological chat activity</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!isLoading && !error && threadSnapshots.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={isTranslating || translationItems.length === 0}
              onClick={() => void handleTranslateClick()}
            >
              {isTranslating ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Languages className="size-4" aria-hidden="true" />
              )}
              {translateButtonLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={isFetching && !isFetchingNextPage}
            onClick={() => {
              setTranslationRetryGeneration((generation) => generation + 1);
              void refetch();
            }}
          >
            <RefreshCw
              className={`size-4 ${isFetching && !isFetchingNextPage ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <Table className="min-w-[640px] table-fixed">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[150px] pl-4">Latest activity</TableHead>
              <TableHead>Thread</TableHead>
              <TableHead className="w-[96px]">Messages</TableHead>
              <TableHead className="w-[64px] pr-4 text-right">
                <span className="sr-only">Project link</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index} className="hover:bg-transparent">
                  <TableCell className="pl-4">
                    <Skeleton className="h-12 w-36 rounded-md" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-12 w-full rounded-md" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-12 w-28 rounded-md" />
                  </TableCell>
                  <TableCell className="pr-4">
                    <Skeleton className="ml-auto h-8 w-24 rounded-md" />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading &&
              threadSnapshots.map((threadSnapshot) => (
                <ProjectChatFeedRow
                  key={threadSnapshot.thread.threadId}
                  threadSnapshot={threadSnapshot}
                  canOpenProjects={canOpenProjects}
                  translationsById={translationsById}
                  showTranslations={showTranslations}
                />
              ))}
          </TableBody>
        </Table>

        {error && (
          <div className="border-t px-4 py-3 text-sm text-destructive">
            {error instanceof Error ? error.message : "Failed to load project chats"}
          </div>
        )}

        {!isLoading && !error && threadSnapshots.length === 0 && (
          <div className="border-t px-4 py-10 text-center text-sm text-muted-foreground">
            {hasNextPage
              ? "No completed chats on this page. Load more to continue."
              : "No completed project chats found"}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          Showing {threadSnapshots.length} thread{threadSnapshots.length === 1 ? "" : "s"}
        </div>
        <Button
          type="button"
          variant="outline"
          className="min-w-36 gap-1.5"
          disabled={!hasNextPage || isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          {hasNextPage ? "Load more" : "No more chats"}
        </Button>
      </div>
    </div>
  );
}

function ProjectChatFeedRow({
  threadSnapshot,
  canOpenProjects,
  translationsById,
  showTranslations,
}: {
  threadSnapshot: ProjectChatFeedItem;
  canOpenProjects: boolean;
  translationsById: ProjectChatTranslationMap;
  showTranslations: boolean;
}) {
  const projectId = threadSnapshot.thread.projectId;
  const latestActivityAt = parseDateTime(threadSnapshot.latestActivityAt);
  const cachedUserPreviewTranslation =
    translationsById[getThreadPreviewTranslationId(threadSnapshot, "user")];
  const userPreviewTranslation =
    cachedUserPreviewTranslation?.sourceText === threadSnapshot.previews.user.trim()
      ? cachedUserPreviewTranslation
      : undefined;
  const cachedAssistantPreviewTranslation =
    translationsById[getThreadPreviewTranslationId(threadSnapshot, "assistant")];
  const assistantPreviewTranslation =
    cachedAssistantPreviewTranslation?.sourceText === threadSnapshot.previews.assistant.trim()
      ? cachedAssistantPreviewTranslation
      : undefined;
  const creditsAreApproximate = getCreditsAreApproximate(threadSnapshot.latestTurn);
  const creditsTitle = getCreditsTitle(threadSnapshot.latestTurn);

  return (
    <TableRow>
      <TableCell className="pl-4 align-top">
        <div className="font-medium tabular-nums">{formatRelativeTime(latestActivityAt)}</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {formatTimestamp(latestActivityAt)}
        </div>
        <div className="mt-3 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <User className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{threadSnapshot.thread.createdByUserId}</span>
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {projectId ? (
              <CopyableIdBadge value={projectId} label="Project ID" />
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                Missing project ID
              </Badge>
            )}
            <CopyableIdBadge value={threadSnapshot.thread.threadId} label="Thread ID" />
          </div>
          <div className="mt-3 grid max-w-[640px] gap-2 whitespace-normal">
            <ChatPreviewSnippet
              speaker="user"
              text={threadSnapshot.previews.user}
              translation={userPreviewTranslation}
              showTranslations={showTranslations}
            />
            <ChatPreviewSnippet
              speaker="assistant"
              text={threadSnapshot.previews.assistant}
              translation={assistantPreviewTranslation}
              showTranslations={showTranslations}
            />
          </div>
          {threadSnapshot.latestTurn && (
            <div className="mt-3 flex min-w-0 flex-wrap items-center gap-1.5 text-xs">
              <Badge variant="outline" title={creditsTitle}>
                Credits {creditsAreApproximate ? "≈" : ""}
                {formatTurnMetric(threadSnapshot.latestTurn.creditsUsed)}
              </Badge>
              <Badge
                variant="outline"
                title={
                  threadSnapshot.latestTurn.latencySeconds == null
                    ? "Latency is not available for this turn"
                    : undefined
                }
              >
                Latency{" "}
                {threadSnapshot.latestTurn.latencySeconds == null
                  ? "—"
                  : `${formatTurnMetric(threadSnapshot.latestTurn.latencySeconds)}s`}
              </Badge>
              {threadSnapshot.latestTurn.cancelled && (
                <Badge variant="destructive">Cancelled</Badge>
              )}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex min-w-0 flex-col items-start gap-2">
          <Badge variant="outline" className="gap-1.5">
            <MessageSquare className="size-3.5" aria-hidden="true" />
            {threadSnapshot.messageCount}
          </Badge>
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {threadSnapshot.mutationCount > 0 && (
              <Badge className="border-amber-200 bg-amber-50 text-amber-900" variant="outline">
                {threadSnapshot.mutationCount} change{threadSnapshot.mutationCount === 1 ? "" : "s"}
              </Badge>
            )}
            {threadSnapshot.committedMutationCount > 0 && (
              <Badge
                className="border-emerald-200 bg-emerald-50 text-emerald-900"
                variant="outline"
              >
                <GitCommit className="size-3" aria-hidden="true" />
                {threadSnapshot.committedMutationCount} committed
              </Badge>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="pr-4 text-right align-top">
        {projectId && canOpenProjects ? (
          <Button variant="ghost" size="icon" asChild className="size-8" title="Open project">
            <Link to="/projects/$projectId" params={{ projectId }}>
              <ExternalLink className="size-3.5" aria-hidden="true" />
              <span className="sr-only">Open project</span>
            </Link>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            disabled
            title="Open project unavailable"
          >
            <ExternalLink className="size-3.5" aria-hidden="true" />
            <span className="sr-only">Open project unavailable</span>
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function ChatPreviewSnippet({
  speaker,
  text,
  translation,
  showTranslations,
}: {
  speaker: ProjectChatPreviewRole;
  text: string;
  translation?: ProjectChatTranslatedItem;
  showTranslations: boolean;
}) {
  const Icon = speaker === "user" ? User : Bot;
  const label = speaker === "user" ? "User" : "Assistant";
  const fallback = speaker === "user" ? "No user message" : "No assistant message";

  return (
    <div className="min-w-0 border-l border-border pl-2">
      <div className="mb-1 flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Icon className="size-3 shrink-0" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="text-sm leading-5 text-muted-foreground">
        {text ? (
          <TranslatedChatText
            text={text}
            translation={translation}
            showTranslations={showTranslations}
            compact
          />
        ) : (
          fallback
        )}
      </div>
    </div>
  );
}

function CopyableIdBadge({ value, label }: { value?: string | null; label: string }) {
  if (!value) {
    return null;
  }

  return (
    <Badge asChild variant="outline" className="max-w-full cursor-pointer gap-1.5">
      <button
        type="button"
        title={`Copy ${label}: ${value}`}
        aria-label={`Copy ${label}: ${value}`}
        data-copy-id-value={value}
        data-copy-id-label={label}
        onClick={() => {
          void copyToClipboard(value, `${label} copied to clipboard`, `Failed to copy ${label}`);
        }}
      >
        <span className="shrink-0">{label}</span>
        <span className="min-w-0 truncate font-mono text-[11px] font-normal text-muted-foreground">
          {truncateMiddle(value)}
        </span>
        <Copy className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
    </Badge>
  );
}

function TranslatedChatText({
  text,
  translation,
  showTranslations,
  compact = false,
}: {
  text: string;
  translation?: ProjectChatTranslatedItem;
  showTranslations: boolean;
  compact?: boolean;
}) {
  const translatedText = translation?.translatedText.trim();
  const shouldShowTranslation =
    showTranslations && translation?.translated && translatedText && translatedText !== text.trim();

  return (
    <div>
      <div className={`${compact ? "line-clamp-3" : "line-clamp-4"} whitespace-pre-wrap`}>
        {shouldShowTranslation ? translation.translatedText : text}
      </div>
      {shouldShowTranslation && (
        <div className="mt-2 rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">
          <div className="mb-1 font-medium text-foreground">Original</div>
          <div className={`${compact ? "line-clamp-2" : "line-clamp-3"} whitespace-pre-wrap`}>
            {text}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeTime(timestamp: number) {
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return "Unknown";
  }

  const deltaMs = Date.now() - timestamp;
  const absDeltaMs = Math.abs(deltaMs);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  if (absDeltaMs < minuteMs) {
    return "Just now";
  }

  if (absDeltaMs < hourMs) {
    return formatRelativeUnit(deltaMs, minuteMs, "min", "min");
  }

  if (absDeltaMs < dayMs) {
    return formatRelativeUnit(deltaMs, hourMs, "hour", "hours");
  }

  return formatRelativeUnit(deltaMs, dayMs, "day", "days");
}

function formatRelativeUnit(deltaMs: number, unitMs: number, singular: string, plural: string) {
  const value = Math.max(1, Math.round(Math.abs(deltaMs) / unitMs));
  const unit = value === 1 ? singular : plural;
  return deltaMs >= 0 ? `${value} ${unit} ago` : `in ${value} ${unit}`;
}

function truncateMiddle(value: string, size = 8) {
  if (value.length <= size * 2 + 3) {
    return value;
  }

  return `${value.slice(0, size)}...${value.slice(-size)}`;
}

function formatTurnMetric(value: number | null | undefined) {
  return value == null
    ? "—"
    : new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

function getCreditsAreApproximate(turn: ProjectChatFeedItem["latestTurn"]) {
  return (
    turn?.creditsAreApproximate ?? (turn?.creditsUsed != null && Number.isInteger(turn.creditsUsed))
  );
}

function getCreditsTitle(turn: ProjectChatFeedItem["latestTurn"]) {
  if (turn?.creditsUsed == null) {
    return "Credits were not recorded for this turn";
  }
  if (getCreditsAreApproximate(turn)) {
    return "Only whole-credit precision is available for this turn";
  }
  return;
}
